import { Component } from '@theme/component';

/**
 * Floating WhatsApp button (sections/whatsapp-button.liquid).
 *
 * The link works without this file. All this does is lift the button above a
 * sticky `.bundle-bar` (the homepage bundle tray on phones) while that bar sits
 * over the bottom of the viewport, so the two never overlap. It does the same
 * for the hero offer slide's trust line and SHOP NOW button, which sit at the
 * bottom of the first screen on phones.
 *
 * One IntersectionObserver watches the bars against the bottom 10% of the
 * viewport. No scroll listener.
 */
class WhatsappButtonComponent extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {Set<Element>} */
  #covering = new Set();

  connectedCallback() {
    super.connectedCallback();

    const bars = document.querySelectorAll('.bundle-bar, .hero-knewed-offer__actions');
    if (!bars.length) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#covering.add(entry.target);
          else this.#covering.delete(entry.target);
        }
        this.#update();
      },
      { rootMargin: '-90% 0px 0px 0px' }
    );

    bars.forEach((bar) => this.#observer?.observe(bar));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = undefined;
    this.#covering.clear();
  }

  #update() {
    let lift = 0;
    for (const bar of this.#covering) {
      if (bar.classList.contains('bundle-bar')) {
        lift = Math.max(lift, /** @type {HTMLElement} */ (bar).offsetHeight);
      } else {
        lift = Math.max(lift, this.#clearance(bar));
      }
    }
    this.style.setProperty('--whatsapp-lift', `${lift}px`);
  }

  /**
   * Lift needed to sit 12px above an element that is not pinned to the
   * viewport bottom, or 0 when the two do not share any horizontal space.
   * @param {Element} element
   */
  #clearance(element) {
    const link = this.firstElementChild;
    if (!link) return 0;

    const own = link.getBoundingClientRect();
    const other = element.getBoundingClientRect();
    if (other.right <= own.left || other.left >= own.right) return 0;

    // Resting position from the computed inset, which ignores the lift
    // transform, so a measurement taken mid-transition is still correct.
    const restingBottom = window.innerHeight - (parseFloat(getComputedStyle(this).bottom) || 0);
    // The hero actions only reach the bottom of the screen near the top of the
    // page, so measure them at scroll 0. Measuring where they are right now
    // would lock in a mid-scroll value during smooth scrolling.
    const otherTop = other.top + window.scrollY;
    return Math.max(0, Math.ceil(restingBottom - otherTop + 12));
  }
}

if (!customElements.get('whatsapp-button-component')) {
  customElements.define('whatsapp-button-component', WhatsappButtonComponent);
}
